import { Button, DatePicker, TextInput, Tile } from '@Components/common';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styles from './KeyPersonForm.module.scss';
import { RiArrowLeftLine, RiDeleteBinLine } from 'react-icons/ri';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { KeyPersonRoleSelect } from './Components/KeyPersonRoleSelect';
import { KeyPerson, KeyPersonRole } from '@Types/KeyPeople';
import { AddressSection, SectionHeader, ConfirmModal } from '@Components';
import { msgFieldRequired } from '@Helpers';
import classNames from 'classnames';
import { Link } from '@Components/common';
import { debounce } from 'lodash';

export const KeyPersonForm = ({
  closeForm,
  keyPeopleRoles,
  setKeyPeople,
  keyPeople,
  setKeyPerson,
  keyPerson,
}: {
  closeForm: () => void;
  keyPeopleRoles: KeyPersonRole[];
  setKeyPeople: (keyPeople: KeyPerson[]) => void;
  keyPeople: KeyPerson[];
  setKeyPerson: (keyPerson: KeyPerson | undefined) => void;
  keyPerson?: KeyPerson;
}) => {
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false);
  const [duplicateKeyPerson, setDuplicateKeyPerson] = useState<KeyPerson | null>(null);

  // defaultValues is used to load the keyPerson data into the form when editing a keyPerson
  // Have to extend the type to include roles as it is not included in the KeyPerson type
  // but is required to set the error in the form
  const methods = useForm<KeyPerson & { roles?: string }>({
    defaultValues: keyPerson || {},
  });
  const { clearErrors, setError, reset } = methods;

  const isRepresentative = useWatch({
    control: methods.control,
    name: 'is_representative',
  });
  const isOwner = useWatch({
    control: methods.control,
    name: 'is_owner',
  });
  const isDirector = useWatch({
    control: methods.control,
    name: 'is_director',
  });
  const isExecutive = useWatch({
    control: methods.control,
    name: 'is_executive',
  });

  const email = useWatch({
    control: methods.control,
    name: 'email',
  }) as string;

  const { t } = useTranslation('account');

  const checkDuplicateEmail = useCallback(
    (email: string) => {
      const debouncedCheck = debounce((email: string) => {
        if (email) {
          const person = keyPeople.find(
            (person) => person.email.toLowerCase() === email.toLowerCase(),
          );
          if (person && person !== keyPerson) {
            setDuplicateKeyPerson(person);
            setError('email', {
              type: 'duplicate_key_person',
            });
          } else {
            setDuplicateKeyPerson(null);
            clearErrors('email');
          }
        }
      }, 300);
      debouncedCheck(email);
      return () => debouncedCheck.cancel();
    },
    [keyPerson, keyPeople, setError, clearErrors],
  );

  useEffect(() => {
    checkDuplicateEmail(email);
  }, [email, checkDuplicateEmail]);

  useEffect(() => {
    if (isOwner || isRepresentative || isDirector || isExecutive) {
      clearErrors('roles');
    }
  }, [isOwner, isRepresentative, isDirector, isExecutive, clearErrors]);

  const resetAndCloseForm = useCallback(() => {
    setKeyPerson(undefined);
    closeForm();
  }, [closeForm, setKeyPerson]);

  const onSubmit = methods.handleSubmit(
    (data) => {
      // Check that at least one role is selected
      if (!data.is_representative && !data.is_owner && !data.is_director && !data.is_executive) {
        setError('roles', {
          type: 'custom',
          message: t('tabs.organization.add_key_people.roles_required'),
        });
        return;
      }
      // if keyPerson exists in keypeople update the existing entry if not add it as a new keyPerson
      const newKeyPerson: KeyPerson = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        date_of_birth: data.date_of_birth,
        is_representative: data.is_representative || false,
        is_owner: data.is_owner || false,
        is_director: data.is_director || false,
        is_executive: data.is_executive || false,
        address1: data.address1,
        address2: data.address2,
        address3: data.address3,
        city: data.city,
        county: data.county,
        postcode: data.postcode,
      };

      if (keyPerson) {
        const updatedKeyPeople = keyPeople.map((person) =>
          person === keyPerson ? newKeyPerson : person,
        );
        setKeyPeople(updatedKeyPeople);
      } else {
        setKeyPeople([...keyPeople, newKeyPerson]);
      }
      resetAndCloseForm();
    },
    () => {
      // Check that at least one role is selected
      if (!isRepresentative && !isOwner && !isDirector && !isExecutive) {
        setError('roles', {
          type: 'custom',
          message: t('tabs.organization.add_key_people.roles_required'),
        });
        return;
      }
    },
  );

  const handleRemoveKeyPerson = useCallback(() => {
    const updatedKeyPeople = keyPeople.filter((person) => person !== keyPerson);
    setKeyPeople(updatedKeyPeople);
    resetAndCloseForm();
  }, [keyPeople, setKeyPeople, resetAndCloseForm, keyPerson]);

  // Temp saving variable waiting for api call for this
  const isSaving = false;

  return (
    <>
      <Button
        buttonType="text"
        onClick={() => {
          resetAndCloseForm();
        }}
        startIcon={<RiArrowLeftLine />}
      >
        {t('back')}
      </Button>
      <FormProvider {...methods}>
        <form onSubmit={onSubmit} className={styles.formContainer}>
          <Tile
            title={{
              title: !keyPerson
                ? t('tabs.organization.add_key_people.title')
                : t('tabs.organization.add_key_people.edit_title'),
            }}
            className={styles.container}
          >
            <div className={styles.container}>
              <div>
                <SectionHeader
                  title={t('tabs.organization.add_key_people.basic_information.title')}
                />
                <div className={styles.nameContainer}>
                  <TextInput
                    label={t('tabs.organization.add_key_people.basic_information.email')}
                    inputRef={methods.register({ required: msgFieldRequired })}
                    name="email"
                    error={!!methods.errors.email}
                    helperText={
                      methods.errors.email?.type === 'duplicate_key_person' ? (
                        <Trans
                          ns="account"
                          i18nKey="tabs.organization.add_key_people.duplicate_email"
                          components={[
                            <Link
                              onClick={() => {
                                if (duplicateKeyPerson) {
                                  setKeyPerson(duplicateKeyPerson);
                                  reset(duplicateKeyPerson);
                                  clearErrors('email');
                                }
                              }}
                              underline
                            />,
                          ]}
                        />
                      ) : (
                        methods.errors.email?.message
                      )
                    }
                    disabled={isSaving}
                  />
                  <DatePicker
                    name="date_of_birth"
                    required={true}
                    label={t('tabs.organization.add_key_people.basic_information.date_of_birth')}
                    error={!!methods.errors.date_of_birth}
                    helperText={methods.errors.date_of_birth?.message}
                    disabled={isSaving}
                    openTo="year"
                  />
                </div>
                <div className={styles.nameContainer}>
                  <TextInput
                    label={t('tabs.organization.add_key_people.basic_information.first_name')}
                    inputRef={methods.register({ required: msgFieldRequired })}
                    name="first_name"
                    error={!!methods.errors.first_name}
                    helperText={methods.errors.first_name?.message}
                    disabled={isSaving}
                    InputLabelProps={{ shrink: !!methods.watch('first_name') }}
                  />
                  <TextInput
                    label={t('tabs.organization.add_key_people.basic_information.last_name')}
                    inputRef={methods.register({ required: msgFieldRequired })}
                    name="last_name"
                    error={!!methods.errors.last_name}
                    helperText={methods.errors.last_name?.message}
                    disabled={isSaving}
                    InputLabelProps={{ shrink: !!methods.watch('last_name') }}
                  />
                </div>
              </div>

              <div>
                <SectionHeader title={t('tabs.organization.add_key_people.home_address')} />
                <AddressSection countyRequired isLoading={false} required />
              </div>
              <div>
                <SectionHeader
                  title={t('tabs.organization.add_key_people.roles')}
                  subtitle={t('tabs.organization.add_key_people.roles_description')}
                />
                <div
                  className={classNames(
                    styles.roleSelect,
                    methods.errors.roles && styles.roleSelectError,
                  )}
                >
                  {keyPeopleRoles.map((role) => (
                    <Controller
                      key={role.name}
                      name={`is_${role.name.toLowerCase()}`}
                      control={methods.control}
                      render={({ value, onChange, ref }) => {
                        return (
                          <KeyPersonRoleSelect
                            inputRef={ref}
                            value={ref.current.checked}
                            checked={Boolean(value)}
                            name={role.name}
                            description={role.description}
                            onChange={(e) => {
                              onChange(e.target.checked);
                            }}
                            required={!!methods.errors.roles}
                            disabled={
                              role.name.toLowerCase() === 'representative' &&
                              keyPeople.some((person) => person.is_representative)
                            }
                          />
                        );
                      }}
                    />
                  ))}
                </div>
                {methods.errors.roles && (
                  <p className={styles.roleSelectErrorText}>
                    {t('tabs.organization.add_key_people.roles_required')}
                  </p>
                )}
              </div>
            </div>
          </Tile>
          <div className={styles.buttonsContainer}>
            {keyPerson && (
              <Button
                buttonType="danger"
                className={styles.deletePersonButton}
                startIcon={<RiDeleteBinLine />}
                onClick={() => setIsRemoveModalVisible(true)}
              >
                {t('tabs.organization.add_key_people.remove_person')}
              </Button>
            )}
            <Button
              buttonType="primary"
              type="submit"
              className={styles.button}
              disabled={methods.errors.email?.type === 'duplicate_key_person'}
            >
              {keyPerson
                ? t('tabs.organization.add_key_people.update_person')
                : t('tabs.organization.add_key_people.add_person')}
            </Button>
          </div>
        </form>
      </FormProvider>
      <ConfirmModal
        isOpen={isRemoveModalVisible}
        onClose={() => setIsRemoveModalVisible(false)}
        title={t('tabs.organization.add_key_people.remove_modal.title')}
        cancelText={t('tabs.organization.add_key_people.remove_modal.cancel')}
        onCancel={() => setIsRemoveModalVisible(false)}
        confirmText={t('tabs.organization.add_key_people.remove_modal.confirm')}
        onConfirm={handleRemoveKeyPerson}
        isConfirmBtnDanger
      >
        {t('tabs.organization.add_key_people.remove_modal.text')}
      </ConfirmModal>
    </>
  );
};
